import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Save } from 'lucide-react';
import {
  userGroupsService,
  Permissions,
  SYSTEM_RESOURCES,
} from '@/services/user-groups.service';

export function UserGroupFormPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const queryClient = useQueryClient();
  const isEditing = !!id;

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [active, setActive] = useState(true);
  const [permissions, setPermissions] = useState<Permissions>({});

  const { data: group, isLoading } = useQuery({
    queryKey: ['user-group', id],
    queryFn: () => userGroupsService.getById(id!),
    enabled: isEditing,
  });

  useEffect(() => {
    if (group) {
      setName(group.name);
      setDescription(group.description || '');
      setActive(group.active);
      setPermissions(group.permissions);
    }
  }, [group]);

  const createMutation = useMutation({
    mutationFn: userGroupsService.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-groups'] });
      navigate('/app/grupos');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      userGroupsService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-groups'] });
      queryClient.invalidateQueries({ queryKey: ['user-group', id] });
      navigate('/app/grupos');
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const data = {
      name,
      description: description || undefined,
      permissions,
      active,
    };

    try {
      if (isEditing) {
        await updateMutation.mutateAsync({ id: id!, data });
      } else {
        await createMutation.mutateAsync(data);
      }
    } catch (error: any) {
      alert(error.response?.data?.message || 'Erro ao salvar grupo');
    }
  };

  const handlePermissionChange = (
    resource: string,
    action: 'view' | 'create' | 'edit' | 'delete',
    value: boolean
  ) => {
    setPermissions((prev) => ({
      ...prev,
      [resource]: {
        ...prev[resource as keyof Permissions],
        [action]: value,
      },
    }));
  };

  const handleSelectAll = (action: 'view' | 'create' | 'edit' | 'delete') => {
    const newPermissions: Permissions = {};
    SYSTEM_RESOURCES.forEach((resource) => {
      newPermissions[resource.key as keyof Permissions] = {
        ...permissions[resource.key as keyof Permissions],
        [action]: true,
      };
    });
    setPermissions(newPermissions);
  };

  const handleDeselectAll = (action: 'view' | 'create' | 'edit' | 'delete') => {
    const newPermissions: Permissions = {};
    SYSTEM_RESOURCES.forEach((resource) => {
      newPermissions[resource.key as keyof Permissions] = {
        ...permissions[resource.key as keyof Permissions],
        [action]: false,
      };
    });
    setPermissions(newPermissions);
  };

  if (isEditing && isLoading) {
    return <div className="p-8 text-center">Carregando...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/app/grupos')}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            {isEditing ? 'Editar Grupo' : 'Novo Grupo'}
          </h1>
          <p className="text-gray-600 mt-1">
            Configure as permissões de acesso do grupo
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-white rounded-lg shadow p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">
            Informações Básicas
          </h2>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nome do Grupo *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Ex: Vendedores, Gerentes, Suporte..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Descrição
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Descreva o propósito deste grupo..."
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="active"
              checked={active}
              onChange={(e) => setActive(e.target.checked)}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label htmlFor="active" className="text-sm font-medium text-gray-700">
              Grupo ativo
            </label>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Permissões</h2>
          <p className="text-sm text-gray-600">
            Configure quais ações cada recurso do sistema permite
          </p>

          <div className="overflow-x-auto">
            <table className="w-full border border-gray-200 rounded-lg">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b border-gray-200">
                    Recurso
                  </th>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700 border-b border-gray-200">
                    <div className="flex flex-col items-center gap-1">
                      <span>Visualizar</span>
                      <div className="flex gap-1">
                        <button
                          type="button"
                          onClick={() => handleSelectAll('view')}
                          className="text-xs text-blue-600 hover:text-blue-800"
                        >
                          todos
                        </button>
                        <span className="text-gray-400">|</span>
                        <button
                          type="button"
                          onClick={() => handleDeselectAll('view')}
                          className="text-xs text-blue-600 hover:text-blue-800"
                        >
                          nenhum
                        </button>
                      </div>
                    </div>
                  </th>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700 border-b border-gray-200">
                    <div className="flex flex-col items-center gap-1">
                      <span>Criar</span>
                      <div className="flex gap-1">
                        <button
                          type="button"
                          onClick={() => handleSelectAll('create')}
                          className="text-xs text-blue-600 hover:text-blue-800"
                        >
                          todos
                        </button>
                        <span className="text-gray-400">|</span>
                        <button
                          type="button"
                          onClick={() => handleDeselectAll('create')}
                          className="text-xs text-blue-600 hover:text-blue-800"
                        >
                          nenhum
                        </button>
                      </div>
                    </div>
                  </th>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700 border-b border-gray-200">
                    <div className="flex flex-col items-center gap-1">
                      <span>Editar</span>
                      <div className="flex gap-1">
                        <button
                          type="button"
                          onClick={() => handleSelectAll('edit')}
                          className="text-xs text-blue-600 hover:text-blue-800"
                        >
                          todos
                        </button>
                        <span className="text-gray-400">|</span>
                        <button
                          type="button"
                          onClick={() => handleDeselectAll('edit')}
                          className="text-xs text-blue-600 hover:text-blue-800"
                        >
                          nenhum
                        </button>
                      </div>
                    </div>
                  </th>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700 border-b border-gray-200">
                    <div className="flex flex-col items-center gap-1">
                      <span>Excluir</span>
                      <div className="flex gap-1">
                        <button
                          type="button"
                          onClick={() => handleSelectAll('delete')}
                          className="text-xs text-blue-600 hover:text-blue-800"
                        >
                          todos
                        </button>
                        <span className="text-gray-400">|</span>
                        <button
                          type="button"
                          onClick={() => handleDeselectAll('delete')}
                          className="text-xs text-blue-600 hover:text-blue-800"
                        >
                          nenhum
                        </button>
                      </div>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {SYSTEM_RESOURCES.map((resource) => {
                  const resourcePermissions =
                    permissions[resource.key as keyof Permissions] || {};
                  return (
                    <tr key={resource.key} className="hover:bg-gray-50">
                      <td className="px-4 py-3 border-r border-gray-200">
                        <div>
                          <div className="font-medium text-gray-900">
                            {resource.label}
                          </div>
                          <div className="text-xs text-gray-500">
                            {resource.description}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center border-r border-gray-200">
                        <input
                          type="checkbox"
                          checked={resourcePermissions.view || false}
                          onChange={(e) =>
                            handlePermissionChange(
                              resource.key,
                              'view',
                              e.target.checked
                            )
                          }
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                      </td>
                      <td className="px-4 py-3 text-center border-r border-gray-200">
                        <input
                          type="checkbox"
                          checked={resourcePermissions.create || false}
                          onChange={(e) =>
                            handlePermissionChange(
                              resource.key,
                              'create',
                              e.target.checked
                            )
                          }
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                      </td>
                      <td className="px-4 py-3 text-center border-r border-gray-200">
                        <input
                          type="checkbox"
                          checked={resourcePermissions.edit || false}
                          onChange={(e) =>
                            handlePermissionChange(
                              resource.key,
                              'edit',
                              e.target.checked
                            )
                          }
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                      </td>
                      <td className="px-4 py-3 text-center">
                        <input
                          type="checkbox"
                          checked={resourcePermissions.delete || false}
                          onChange={(e) =>
                            handlePermissionChange(
                              resource.key,
                              'delete',
                              e.target.checked
                            )
                          }
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate('/app/grupos')}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={createMutation.isPending || updateMutation.isPending}
            className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {isEditing ? 'Atualizar' : 'Criar'} Grupo
          </button>
        </div>
      </form>
    </div>
  );
}
